/**
 * Services API pour le module Commandes
 * Gère les appels API et la transformation des filtres
 */

// ============================================================================
// Interface pour les filtres UI
// ============================================================================

export interface FilterOptions {
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  lotTechnique: string;
  lotGeographique: string;
  createur: string;
  nomOperation: string;
  entite: string; // Nom de l'entité (organisme pour prestataire, entité pour acheteur)
  prixRange: {
    min: number;
    max: number;
  };
  status: string;
  type: string;
}

// ============================================================================
// Interface pour les paramètres API
// ============================================================================

/**
 * Paramètres de requête API pour les commandes
 * Ces noms correspondent aux QueryFilters côté backend (BdcFilters.php)
 * Les filtres commande doivent avoir le préfixe 'commande_'
 */
export interface ApiCommandeFilters {
  // Pagination
  page?: number;
  per_page?: number;

  // Filtres par statut (communs)
  status_id?: string;
  status_action?: string;
  status_type_compte?: string;
  status_nom?: string;

  // Filtres par commande (préfixe commande_)
  commande_nom?: string;
  commande_lot_technique?: string | number;
  commande_lot_departement?: string | number;
  commande_prix_total_min?: number;
  commande_prix_total_max?: number;
  commande_id_entite?: string | number;
  commande_type_compte?: string;

  // Filtres par opération (préfixe operation_)
  operation_nom?: string;
  operation_lot_technique?: string | number;
  operation_lot_departement?: string | number;
  operation_organisme_id?: string | number;

  // Filtre par type (BC/MS)
  type?: string;
}

// ============================================================================
// Transformation des filtres UI -> Paramètres API
// ============================================================================

/**
 * Transforme les filtres de l'interface utilisateur en paramètres API
 *
 * Mapping:
 * - lotTechnique -> id_lot_technique
 * - lotGeographique -> id_lot_departement
 * - nomOperation -> nom
 * - client -> organisme_nom (recherche texte)
 * - prixRange.min -> prix_total_min
 * - nomOperation -> commande_nom (ou operation_nom selon contexte)
 * - lotTechnique -> commande_lot_technique
 * - lotGeographique -> commande_lot_departement
 * - prixRange.min -> commande_prix_total_min
 * - prixRange.max -> commande_prix_total_max
 * - status -> status_id (ou status_nom pour recherche par nom)
 * - type -> type (BC/MS)
 *
 * Note: Les filtres utilisent le préfixe 'commande_' pour correspondre au backend (BdcFilters.php)
 */
export const transformFiltersToApiParams = (
  filters: Partial<FilterOptions>,
  page: number = 1,
  perPage: number = 50
): ApiCommandeFilters => {
  const params: ApiCommandeFilters = {
    page,
    per_page: perPage,
  };

  // Filtre par nom de commande
  if (filters.nomOperation?.trim()) {
    params.commande_nom = filters.nomOperation.trim();
  }

  // Filtre par lot technique
  // Format attendu: "Lot XXX" où XXX est l'ID
  if (filters.lotTechnique?.trim()) {
    const lotMatch = filters.lotTechnique.match(/Lot\s*(\d+)/i);
    if (lotMatch) {
      params.commande_lot_technique = parseInt(lotMatch[1], 10);
    } else {
      // Peut-être un ID direct ou autre format
      const idMatch = filters.lotTechnique.match(/^(\d+)/);
      if (idMatch) {
        params.commande_lot_technique = parseInt(idMatch[1], 10);
      }
    }
  }

  // Filtre par département (lot géographique)
  if (filters.lotGeographique?.trim()) {
    params.commande_lot_departement = filters.lotGeographique;
  }

  // Filtre par plage de prix
  if (filters.prixRange) {
    if (filters.prixRange.min > 0) {
      params.commande_prix_total_min = filters.prixRange.min;
    }
    if (filters.prixRange.max > 0) {
      params.commande_prix_total_max = filters.prixRange.max;
    }
  }

  // Filtre par statut (par ID)
  if (filters.status?.trim()) {
    params.status_id = filters.status;
  }

  // Filtre par type (BC/MS)
  if (
    filters.type &&
    filters.type !== "Tous" &&
    filters.type !== "Tous les types"
  ) {
    params.type = filters.type;
  }

  return params;
};

// ============================================================================
// Construction de l'URL
// ============================================================================

/**
 * Construit la query string à partir des paramètres
 */
export const buildQueryString = (params: ApiCommandeFilters): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

/**
 * Fusionne les paramètres de base (onglet) avec les filtres utilisateur
 */
export const mergeFiltersWithBaseParams = (
  baseQueryString: string,
  filters: ApiCommandeFilters
): string => {
  const searchParams = new URLSearchParams(baseQueryString);

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      // Ne pas écraser page et per_page si déjà présents dans baseQueryString
      if (key === "page" || key === "per_page") {
        if (!searchParams.has(key)) {
          searchParams.set(key, String(value));
        }
      } else {
        searchParams.set(key, String(value));
      }
    }
  });

  return searchParams.toString();
};
